import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  TextField,
  Typography,
  Box,
  Chip,
  Alert,
  InputAdornment,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  RiCloseLine,
  RiUploadCloudLine,
  RiFileTextLine,
  RiImageLine,
  RiCheckLine,
  RiSearchLine,
  RiErrorWarningLine,
  RiUserSettingsLine,
  RiQuestionLine,
} from "@remixicon/react";
import { useState, type FunctionComponent, useRef, useEffect } from "react";
import { useForm, type SubmitHandler, Controller, useWatch } from "react-hook-form";
import { createActualizarPerfilFormSchema, type ActualizarPerfilSchemaValues } from "./ActualizarPerfilModal.schema";
import { useActualizarPerfil } from "@/shared/hooks/useActualizarPerfil.hook";
import { useValidarMatricula } from "@/shared/hooks/useValidarMatricula.hook";
import customAlert from "../CustomAlert";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import i18n from "i18next";
import 'dayjs/locale/es';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/ht';
import { validarExisteAbogadoPorMatriculaCedula } from "@/modules/login/services/ApiConsultaGestionCasos/Contactos.service";
import { useUserData } from "@/shared/hooks/useUserData.hook";
import { useTranslation } from "react-i18next";
import { fileToBase64 } from "@/modules/login/utils/fileConversion.utils";
import { useAuth, type SystemTime } from "@/shared/contexts/AuthContext";
import HelpModal from "@/shared/components/Help/HelpModal";

interface ActualizarPerfilModalProps {
  closeModal: () => void;
  isOpen: boolean;
}

const ActualizarPerfilModal: FunctionComponent<ActualizarPerfilModalProps> = ({
  closeModal,
  isOpen,
}) => {
  const { logout } = useAuth();
  const { t } = useTranslation('actualizarPerfil');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [matriculaValidada, setMatriculaValidada] = useState<boolean | null>(null);
  const [isValidatingMatricula, setIsValidatingMatricula] = useState(false);
  const [fileValidationStatus, setFileValidationStatus] = useState<boolean | null>(null);
  const [fileValidationMessage, setFileValidationMessage] = useState<string>("");
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const { userData } = useUserData();
  const { identificacion: Cedula } = userData || {};
  const MAX_CARACTERES_JUSTIFICACION = 300;

  const currentLanguage = i18n.language;

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    control,
    setValue,
    trigger,
    watch,
  } = useForm<ActualizarPerfilSchemaValues>({
    resolver: yupResolver(createActualizarPerfilFormSchema(t)),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      matriculaAbogado: "",
      fechaMatricula: "",
      justificacion: "",
      archivoEvidencia: null,
    },
  });

  const watchedMatricula = useWatch({
    control,
    name: "matriculaAbogado",
  });

  const watchedJustificacion = useWatch({
    control,
    name: "justificacion",
  });

  useEffect(() => {
    setMatriculaValidada(null);
  }, [watchedMatricula]);

  const { mutate: actualizarPerfil, isPending: isPendingActualizarPerfil } =
    useActualizarPerfil(t);

  const { isPending: isPendingValidarMatricula } =
    useValidarMatricula();

  const isLoading = isPendingActualizarPerfil || isPendingValidarMatricula;

  const getValidationMessage = () => {
    if (matriculaValidada === null) {
      return t('actualizarPerfil.validationMessages.enterMatricula');
    }
    if (matriculaValidada === true) {
      return t('actualizarPerfil.validationMessages.matriculaValidated');
    }
    if (matriculaValidada === false) {
      return t('actualizarPerfil.validationMessages.matriculaNotFound');
    }
    return t('actualizarPerfil.validationMessages.enterMatricula');
  };

  const getAlertSeverity = () => {
    if (matriculaValidada === true) return "success";
    if (matriculaValidada === false) return "warning";
    return "info";
  };

  const handleValidarMatricula = async () => {
    const matricula = watch("matriculaAbogado");
    if (!matricula || !Cedula) return;

    setIsValidatingMatricula(true);
    try {
      const existe = await validarExisteAbogadoPorMatriculaCedula({
        Matricula: matricula,
        Cedula: Cedula,
      });
      setMatriculaValidada(existe);
    } catch (error) {
      setMatriculaValidada(false);
      console.error("Error validando matrícula:", error);
    } finally {
      setIsValidatingMatricula(false);
    }
  };

  const handleClose = (skipConfirmation: boolean = false) => {
    const isFormDirty = isDirty || selectedFile !== null;
    
    if (isFormDirty && !skipConfirmation) {
      customAlert({
        titulo: t('actualizarPerfil.alerts.unsavedChanges.title'),
        mensaje: t('actualizarPerfil.alerts.unsavedChanges.message'),
        icon: "warning",
        botonCancelar: true,
        textoBotonAceptar: t('actualizarPerfil.alerts.unsavedChanges.confirmButton'),
        textoBotonCancelar: t('actualizarPerfil.alerts.unsavedChanges.cancelButton'),
        colorBotonAceptar: "#d33",
        colorBotonCancelar: "#3085d6",
        callback: (isConfirmed: boolean) => {
          if (isConfirmed) {
            reset();
            setSelectedFile(null);
            setFilePreview(null);
            setMatriculaValidada(null);
            setIsValidatingMatricula(false);
            setFileValidationStatus(null);
            setFileValidationMessage("");
            closeModal();
          }
        }
      });
    } else {
      reset();
      setSelectedFile(null);
      setFilePreview(null);
      setMatriculaValidada(null);
      setIsValidatingMatricula(false);
      setFileValidationStatus(null);
      setFileValidationMessage("");
      closeModal();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const processFile = (file: File) => {
    /* Validar archivo automáticamente */
    const ALLOWED_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    const MAX_FILE_SIZE = 5 * 1024 * 1024; //<-- 5MB

    const isValidType = ALLOWED_FILE_TYPES.includes(file.type);
    const isValidSize = file.size <= MAX_FILE_SIZE;

    let validationMessage = "";
    let isValid = true;

    if (!isValidType) {
      validationMessage = t('actualizarPerfil.fileValidation.invalidType');
      isValid = false;
    } else if (!isValidSize) {
      validationMessage = t('actualizarPerfil.fileValidation.tooLarge');
      isValid = false;
    } else {
      validationMessage = t('actualizarPerfil.fileValidation.valid');
    }

    setFileValidationStatus(isValid);
    setFileValidationMessage(validationMessage);

    setSelectedFile(file);
    setValue("archivoEvidencia", file);
    trigger("archivoEvidencia");

    // Crear preview si es imagen
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    setFileValidationStatus(null);
    setFileValidationMessage("");
    setValue("archivoEvidencia", null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const onSubmit: SubmitHandler<ActualizarPerfilSchemaValues> = async (data) => {
    if (matriculaValidada !== true) {
      customAlert({
        icon: "warning",
        titulo: `<div class="text-sm">${t('actualizarPerfil.alerts.validationRequired.title')}</div>`,
        mensaje: t('actualizarPerfil.alerts.validationRequired.message'),
        tiempo: 5,
        toast: true,
      });
      return;
    }

    customAlert({
      icon: "question",
      titulo: `<div class="text-2xl font-semibold">${t('actualizarPerfil.alerts.confirmUpdate.title')}</div>`,
      mensaje: t('actualizarPerfil.alerts.confirmUpdate.message'),
      botonCancelar: true,
      cerrarEsc: false,
      cerrarClicFuera: false,
      textoBotonAceptar: t('actualizarPerfil.alerts.confirmUpdate.confirmButton'),
      colorBotonAceptar: "#3085d6",
      colorBotonCancelar: "#d33",
      textoBotonCancelar: t('actualizarPerfil.alerts.confirmUpdate.cancelButton'),
      callback: async (isConfirmed) => {
        if (isConfirmed) {
          try {
            /* Convertir archivo a base64 si existe */
            let archivoBase64 = "";
            if (data.archivoEvidencia) {
              archivoBase64 = await fileToBase64(data.archivoEvidencia);
            }

            const requestData = {
              matriculaAbogado: data.matriculaAbogado,
              fechaMatricula: data.fechaMatricula,
              justificacion: data.justificacion,
              archivoEvidencia: archivoBase64,
            };

            actualizarPerfil(requestData, {
              onSuccess: async () => {
                /* Mostrar alerta de reinicio requerido y cerrar modal sin confirmación */
                customAlert({
                  icon: "success",
                  titulo: t('actualizarPerfil.hookMessages.restartRequired.title'),
                  mensaje: `${t('actualizarPerfil.hookMessages.restartRequired.message')}`,
                  textoBotonAceptar: t('actualizarPerfil.hookMessages.restartRequired.confirmButton'),
                  cerrarEsc: false,
                  cerrarClicFuera: false,
                  tiempo: 30,
                  mensajeTiempoSWL: `${t('actualizarPerfil.hookMessages.restartRequired.autoCloseMessage')}`,
                  callback: async () => {
                    handleClose(true);
                    await logout();
                  },
                });
              },
            });
          } catch (error) {
            console.error("Error convirtiendo archivo a base64:", error);
            customAlert({
              icon: "error",
              titulo: t('actualizarPerfil.hookMessages.error.title'),
              mensaje: "Error procesando el archivo. Inténtalo de nuevo.",
              tiempo: 8,
              toast: true,
            });
          }
        }
      },
    });
  };

  const getFileIcon = () => {
    if (!selectedFile) return <RiUploadCloudLine />;
    if (selectedFile.type === "application/pdf") return <RiFileTextLine />;
    return <RiImageLine />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return `0 ${t('actualizarPerfil.fileSizeUnits.bytes')}`;
    const k = 1024;
    const sizes = [t('actualizarPerfil.fileSizeUnits.bytes'), t('actualizarPerfil.fileSizeUnits.kb'), t('actualizarPerfil.fileSizeUnits.mb')];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={currentLanguage}>
      <Dialog
        open={isOpen}
        onClose={(_event, reason) => {
          if (reason === "backdropClick") return;
          handleClose();
        }}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0, 80, 218, 0.15)",
          },
        }}
      >
      <DialogTitle>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <RiUserSettingsLine size={32} color="#0050DA" />
            <div>
              <Typography
                variant="h5"
                component="div"
                fontWeight="700"
                sx={{
                  background: "linear-gradient(135deg, #0050DA 0%, #3B6DF1 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {t('actualizarPerfil.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('actualizarPerfil.subtitle')}
              </Typography>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Tooltip title={t('actualizarPerfil.tooltips.help', 'Ayuda')} arrow>
              <IconButton onClick={() => setIsHelpOpen(true)} size="small">
                <RiQuestionLine />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('actualizarPerfil.tooltips.close')} arrow>
              <IconButton 
                disabled={isLoading}
                onClick={() => handleClose()} size="small">
                <RiCloseLine />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </DialogTitle>

      <Divider />

      <DialogContent className="space-y-6 pt-6">
        <Alert severity={getAlertSeverity()} sx={{ mb: 3 }}>
          <Typography variant="body2">
            {getValidationMessage()}
          </Typography>
        </Alert>

        <form className="flex flex-col gap-6">
          {/* Matrícula de Abogado */}
          <FormControl variant="outlined" fullWidth>
            <TextField
              label={t('actualizarPerfil.matriculaLabel')}
              placeholder={t('actualizarPerfil.matriculaPlaceholder')}
              disabled={isLoading}
              {...register("matriculaAbogado")}
              error={!!errors.matriculaAbogado}
              helperText={errors.matriculaAbogado?.message}
              InputProps={{
                sx: {
                  borderRadius: 2,
                },
                inputProps: {
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  maxLength: 10,
                },
                endAdornment: (
                  <InputAdornment position="end">
                    {matriculaValidada === true && (
                      <RiCheckLine color="green" size={20} />
                    )}
                    {matriculaValidada === false && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Tooltip 
                          arrow
                          title={t('actualizarPerfil.tooltips.matriculaImportant')}
                          placement="top"
                        >
                          <Box sx={{ cursor: 'pointer' }}>
                            <RiErrorWarningLine color="red" size={20} />
                          </Box>
                        </Tooltip>
                      </Box>
                    )}
                    <Tooltip arrow title={t('actualizarPerfil.tooltips.validateMatricula')}>
                      <span>
                        <IconButton
                          onClick={handleValidarMatricula}
                          disabled={isLoading || isValidatingMatricula || !watch("matriculaAbogado")}
                          size="small"
                          sx={{ ml: 1 }}
                        >
                          {isValidatingMatricula ? (
                            <CircularProgress size={20} color="primary" />
                          ) : (
                            <RiSearchLine />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>

          {/* Fecha de Matrícula */}
          <FormControl variant="outlined" fullWidth>
            <Controller
              name="fechaMatricula"
              control={control}
              render={({ field }) => (
                <Tooltip arrow title={t('actualizarPerfil.tooltips.fechaMatricula')}>
                  <DatePicker
                    label={t('actualizarPerfil.fechaMatriculaLabel')}
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(newValue) => {
                      field.onChange(newValue ? dayjs(newValue).format("YYYY-MM-DD") : "");
                    }}
                    disabled={isLoading}
                    maxDate={dayjs()}
                    slotProps={{
                      textField: {
                        error: !!errors.fechaMatricula,
                        helperText: errors.fechaMatricula?.message,
                        fullWidth: true,
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        },
                      },
                    }}
                  />
                </Tooltip>
              )}
            />
          </FormControl>

          {/* Archivo de Evidencia */}
          <FormControl variant="outlined" fullWidth error={!!errors.archivoEvidencia}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: "none" }}
              disabled={isLoading}
            />

            <Tooltip arrow placement="top" title={t('actualizarPerfil.tooltips.fileUpload')}>
              <Box
                onClick={() => !selectedFile && fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                sx={{
                  border: errors.archivoEvidencia
                    ? "2px dashed #d32f2f"
                    : "2px dashed #0050DA",
                  borderRadius: 2,
                  padding: 3,
                  textAlign: "center",
                  cursor: selectedFile ? "default" : "pointer",
                  backgroundColor: selectedFile ? "#f8f9fa" : "#fafbff",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: selectedFile ? "#f8f9fa" : "#f0f4ff",
                    borderColor: selectedFile ? "#0050DA" : "#3B6DF1",
                  },
                }}
              >
              {!selectedFile ? (
                <Box>
                  <RiUploadCloudLine
                    size={48}
                    color="#0050DA"
                    style={{ marginBottom: 8 }}
                  />
                  <Typography variant="body1" fontWeight="600" color="primary">
                    {t('actualizarPerfil.uploadLabel')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('actualizarPerfil.uploadHint')}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {filePreview ? (
                    <img
                      src={filePreview}
                      alt={t('actualizarPerfil.preview')}
                      style={{
                        maxWidth: "200px",
                        maxHeight: "150px",
                        objectFit: "contain",
                        margin: "0 auto 12px",
                        borderRadius: 8,
                      }}
                    />
                  ) : (
                    getFileIcon()
                  )}
                  <Typography variant="body2" fontWeight="600" sx={{ mb: 1 }}>
                    {selectedFile.name}
                  </Typography>
                  <Chip
                    label={formatFileSize(selectedFile.size)}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ mb: 1 }}
                  />
                  {/* {fileValidationStatus === true && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <RiCheckLine color="green" size={16} />
                      <Typography variant="caption" color="success.main" fontWeight="500">
                        {fileValidationMessage}
                      </Typography>
                    </Box>
                  )}
                  {fileValidationStatus === false && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <RiErrorWarningLine color="red" size={16} />
                      <Typography variant="caption" color="error.main" fontWeight="500">
                        {fileValidationMessage}
                      </Typography>
                    </Box>
                  )} */}
                  <Box>
                    <Tooltip arrow placement="bottom" title={t('actualizarPerfil.tooltips.removeFileTooltip')}>
                      <Button
                        size="small"
                        color="error"
                        onClick={handleRemoveFile}
                        disabled={isLoading}
                      >
                        {t('actualizarPerfil.tooltips.removeFileButton')}
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>
              )}
            </Box>
            </Tooltip>
            {errors.archivoEvidencia && (
              <FormHelperText error>
                {errors.archivoEvidencia.message?.toString()}
              </FormHelperText>
            )}
          </FormControl>

          {/* Justificación */}
          <FormControl variant="outlined" fullWidth>
            <Tooltip arrow placement="top" title={t('actualizarPerfil.tooltips.justificationHelp')}>
              <TextField
                label={t('actualizarPerfil.justificacionLabel')}
                placeholder={t('actualizarPerfil.justificacionPlaceholder')}
                multiline
                rows={4}
                disabled={isLoading}
                {...register("justificacion")}
                error={!!errors.justificacion}
                helperText={
                  errors.justificacion?.message ||
                  t('actualizarPerfil.characterCount', { count: watchedJustificacion?.length || 0, max: MAX_CARACTERES_JUSTIFICACION })
                }
                InputProps={{
                  sx: {
                    borderRadius: 2,
                  },
                }}
              />
            </Tooltip>
          </FormControl>
        </form>
      </DialogContent>

      <Divider />

      <DialogActions className="py-4 px-6">
        <Tooltip arrow placement="top" title={t('actualizarPerfil.tooltips.close')}>
          <Button
            variant="outlined"
            color="error"
            onClick={() => handleClose()}
            disabled={isLoading || isValidatingMatricula}
            sx={{ borderRadius: 2 }}
          >
            {t('actualizarPerfil.tooltips.cancelButton')}
          </Button>
        </Tooltip>
        <Tooltip arrow placement="top" title={t('actualizarPerfil.tooltips.updateProfile')}>
          <Button
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            disabled={isLoading || isValidatingMatricula}
            startIcon={
              isLoading || isValidatingMatricula ? (
                <CircularProgress size={20} color="inherit"/>
              ) : (
                <RiCheckLine />
              )
            }
            sx={{
              borderRadius: 2,
              minWidth: 180, // Ancho mínimo fijo para evitar cambios de tamaño
              background: "linear-gradient(135deg, #0050DA 0%, #3B6DF1 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #003da8 0%, #2d5ac9 100%)",
              },
            }}
          >
            {isLoading ? t('actualizarPerfil.updating') : isValidatingMatricula ? t('actualizarPerfil.validating') : t('actualizarPerfil.updateButton')}
          </Button>
        </Tooltip>
      </DialogActions>
    </Dialog>
    <HelpModal
      isOpen={isHelpOpen}
      onClose={() => setIsHelpOpen(false)}
      tourId="n0dhbgFZnAuIME83qeYZ" 
    />
    </LocalizationProvider>
  );
};

export default ActualizarPerfilModal;
